import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { AnySchema } from 'yup';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { Option } from '../../../types';
import { InputWithValidateProps } from './text-input-with-validate';
import { FormControlLabel, FormLabel, Radio, RadioGroup, RadioGroupProps } from '@mui/material';
import { useFieldValidation } from '../../../hooks/formValidate';

export type RadioOptions = {
  options: Option[];
  optionsText: string[];
};

export type SelectWithValidateProps<T> = RadioGroupProps &
  InputWithValidateProps & {
    label?: string;
    validation?: AnySchema;
    value?: Option;
    name: keyof T & string;
    options: Option[];
    optionsText: string[];
    onValue?: (key: string, value: Option | null) => void;
  };

function RadioWithValidate<T>({
  value,
  name,
  label,
  onValue,
  onValidation,
  validation,
  hideValidationError,
  options,
  optionsText,
  ...props
}: SelectWithValidateProps<T>) {
  const { doValidate, errorMessage } = useFieldValidation();
  const [controlledValue, setControlledValue] = useState<Option>(value ?? '');

  useEffect(() => {
    setControlledValue(value ?? '');
  }, [value]);

  useEffect(() => {
    const validate = async () => {
      if (validation) {
        const isValid = await doValidate(controlledValue, validation);
        onValidation?.(name, isValid, validation);
      }
    };
    validate();
  }, [controlledValue, doValidate, name, onValidation, validation]);

  const onChangeEvent = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setControlledValue(value);
      if (onValue) {
        onValue(name, value);
      }
    },
    [name, onValue]
  );

  const renderRadio = useCallback(
    (option: Option, index: number) => {
      const text = optionsText ? optionsText[index] : option;
      return (
        <FormControlLabel
          key={`${name}-radio-${index}`}
          value={option}
          control={<Radio />}
          label={text}
        />
      );
    },
    [name, optionsText]
  );

  return (
    <FormControl
      variant="outlined"
      sx={{
        ...props.sx,
      }}
    >
      <FormLabel>{label}</FormLabel>
      <RadioGroup
        value={controlledValue as Option}
        onChange={(event) => onChangeEvent(event)}
        name="radio-buttons-group"
        {...props}
      >
        {options.map((option, index) => renderRadio(option, index))}
      </RadioGroup>
      <FormHelperText error>{!hideValidationError ? errorMessage : null}</FormHelperText>
    </FormControl>
  );
}

export default RadioWithValidate;
